import React, { useEffect, useState } from 'react'
import { useGlobal } from 'reactn'
import { useLocation, useParams } from 'react-router-dom'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { parse } from 'date-fns'
import {
  EligibilityVerificationUrlSettingProps,
  MedicalInstitutionProps,
} from 'types/applicant'

import { AppBarPatient } from 'ui/partials/AppBar'
import { Layout } from 'ui/layout/Layout'
import {
  VerificationResponse,
  buildEligibilityVerificationUrl,
} from './OnlineEligibilityVerification'
import { getEligibilityVerificationAppId, getMed } from './fetch'
import { ApiErrorWithMessage } from '../client'
import { H1 } from 'ui/shorthands/Texts'
import CenteredContainer from 'ui/layout/CenteredContainer'

export const eligibilityVerificationCanceledUrlPath =
  '/personal/eligibility-verification/cancel'

const cancelWindowName = 'yq_online_eligibility_verification_canceled'

/**
 * 資格確認キャンセル画面に遷移するためのコンポーネント
 * @constructor
 */
export const OnlineEligibilityVerificationCancel: React.FC = () => {
  const { institution_access_key, reservation_date, access_key } = useParams<
    'institution_access_key' | 'reservation_date' | 'access_key'
  >() as {
    institution_access_key: string
    access_key: string
    reservation_date: string
  }

  const [, setError] = useGlobal('error')
  const [med, setMed] = useState<MedicalInstitutionProps | undefined>()
  const [appId, setAppId] = useState<string | undefined>()
  const [cancelUrl, setCancelUrl] = useState<string | undefined>()

  const reservationDate = parse(reservation_date, 'yyyy-MM-dd', new Date())

  useEffect(() => {
    if (med && appId) {
      const setting: EligibilityVerificationUrlSettingProps = {
        url: med?.online_eligibility_verification_url,
        appId,
      }
      const cancelUrl = buildEligibilityVerificationUrl({
        setting,
        reservationDate,
        callbackPath: eligibilityVerificationCanceledUrlPath,
        mode: 'cancel',
      })
      if (cancelUrl) {
        setCancelUrl(cancelUrl)
      } else {
        setError('invalidUrl')
      }
    }
    if (!med) {
      getMed(institution_access_key)
        .then(setMed)
        .catch((e) => {
          if (e instanceof ApiErrorWithMessage) {
            setError('invalidUrl')
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [med, appId])

  useEffect(() => {
    if (med?.online_eligibility_verification_url) {
      getEligibilityVerificationAppId(access_key).then((data) =>
        setAppId(data.online_eligibility_verification_app_id)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [med?.online_eligibility_verification_url])

  if (cancelUrl) {
    // 自分のwindowに名前をつける
    window.name = cancelWindowName
    setTimeout(() => {
      // cancerUrlに遷移する
      window.location.href = cancelUrl
    }, 1)
  }
  return null
}

/**
 * オンライン資格確認キャンセル画面からのコールバック画面
 * @constructor
 */
export const OnlineEligibilityVerificationCancelCallbackScreen: React.FC =
  () => {
    // const [, setError] = useGlobal('error')
    const [message, setMessage] = useState<string | null>(null)
    const { t } = useTranslation()
    const location = useLocation()
    const query = queryString.parse(location.search)

    // iOS18のSafariでwindow.nameがセットされないためwindow.nameチェック機能をコメントアウト
    // const currentWindowName = window.name
    // const isValidWindowName = currentWindowName === cancelWindowName

    useEffect(() => {
      // windowNameをチェックしてコールバック経由ではない場合はエラーを表示する
      // if (isValidWindowName) {
      const { result_code, error_code }: VerificationResponse = {
        result_code: query?.result_code as string,
        error_code: query?.error_code as string,
      }
      let message: string | null = null
      if (result_code === '00') {
        setMessage(t('マイナ保険証の閲覧同意の取り消しが完了しました'))
      } else if (result_code === '09') {
        // NOTE: 資格確認取り消し画面で「オンライン診療等アプリに戻る」をクリックしたとき
        setMessage(t('閲覧同意の取り消しを中止しました'))
      } else {
        message = t('errors.システムエラーが発生しました')
        switch (error_code) {
          case '01':
            message = t('errors.マイナ保険証が未登録です')
            break
          case '02':
            message = t(
              'errors.同一医療機関等の未来日の予約診療があるため処理を完了できませんでした'
            )
            break
          case '03':
            message = t('errors.有効な資格情報が確認できませんでした')
            break
          default:
            break
        }
        setMessage(message)
      }
      // } else {
      //   setError('forbidden')
      // }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    return (
      <Layout header={<AppBarPatient />} showFooter>
        <CenteredContainer maxWidth="sm">
          <H1>{message}</H1>
        </CenteredContainer>
      </Layout>
    )
  }
